<template>
  <div>
    <!-- Selección del método de pago -->
    <div v-if="mode === 'select'" class="grid grid-cols-2 gap-4">
      <button
        v-for="method in payment_methods" :key="method.id" class="btn-blue p-4 text-lg font-medium rounded-lg hover:opacity-90 transition-opacity"
        @click="selectPaymentMethod(method)"
      >
        {{ method.name }}
      </button>
    </div>

    <!-- Formulario de pago -->
    <div v-if="mode === 'form'" class="space-y-6">
      <form @submit.prevent="processPay">
        <!-- Indicador de Cuota Dividida o Saldo Completo Sutil -->
        <div v-if="(splitAmount > 0 || balance > 0)" class="mb-3 px-3 py-2 bg-gray-50 border border-gray-200/80 rounded-xl flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <i class="fas fa-coins text-gray-400 text-xs" />
            <div class="text-[11px] leading-tight">
              <span v-if="splitAmount > 0" class="font-semibold text-gray-800">
                Cuota: ${{ splitAmount.toFixed(2) }}
              </span>
              <span v-else class="font-semibold text-gray-800">
                Saldo: ${{ balance.toFixed(2) }}
              </span>
              <span v-if="change > 0" class="text-[10px] text-gray-400 ml-1.5">
                (Bs. {{ ((splitAmount > 0 ? splitAmount : balance) * change).toFixed(2) }})
              </span>
            </div>
          </div>
          <div class="flex items-center gap-1">
            <button 
              v-if="splitAmount > 0"
              type="button" 
              class="px-2 py-0.5 bg-orange-500 hover:bg-orange-600 text-white text-[10px] font-medium rounded transition-all active:scale-95" 
              @click="applySplitAmount"
            >
              Cuota
            </button>
            <button 
              type="button" 
              class="px-2 py-0.5 bg-white hover:bg-gray-100 text-gray-600 border border-gray-200 text-[10px] font-medium rounded transition-all active:scale-95" 
              @click="applyFullBalance"
            >
              Saldo Total
            </button>
          </div>
        </div>

        <!-- Campo monto en Bs si aplica -->
        <text-input
          v-if="payment_method.change_currency_id" v-model="amountAlternate" type="number" step="0.01" class="mb-4"
          label="Monto en Bolívares" placeholder="Ingrese el monto en Bs" @input="lastEdited = 'bs'"
        />
        <!-- Campo monto principal -->
        <text-input
          v-model="form.amount" :disabled="payment_method.change_currency_id" type="number" step="0.01" class="mb-4"
          label="Monto en USD" placeholder="Ingrese el monto en USD" @input="lastEdited = 'usd'"
        />

        <!-- Campo de tasa de cambio-->
        <text-input
          v-if="payment_method.change_currency_id && change" 
          v-model="change" type="number" step="0.001" class="mb-4" label="Tasa de cambio"
          placeholder="Ingrese la tasa de cambio"
        />

        <!-- Pabilo Verification (Pago Movil Bs) -->
        <div v-if="isPagoMovilBs" class="mb-4 p-4 border rounded-xl bg-orange-50/50 border-orange-100">
          <div class="flex justify-between items-center mb-4">
            <h3 class="font-bold text-gray-800 flex items-center gap-2">
              <i class="fas fa-check-circle text-orange-500" /> Verificación Pabilo
            </h3>
            <label v-if="isAdmin" class="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer">
              <input v-model="bypassPabilo" type="checkbox" class="rounded text-orange-500 focus:ring-orange-500" />
              <span>Ingresar sin verificar (Admin)</span>
            </label>
          </div>
          
          <div v-if="!bypassPabilo">
            <button type="button" class="w-full py-2.5 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors flex justify-center items-center gap-2" :disabled="isVerifying || verifiedPabilo" @click="verifyPabilo">
              <i v-if="isVerifying" class="fas fa-spinner fa-spin" />
              <i v-else-if="verifiedPabilo" class="fas fa-check" />
              <i v-else class="fas fa-shield-alt" />
              <span v-if="isVerifying">Verificando...</span>
              <span v-else-if="verifiedPabilo">Pago verificado correctamente</span>
              <span v-else>Verificar pago ahora</span>
            </button>
            <p v-if="pabiloError" class="text-xs text-red-500 mt-2 text-center">{{ pabiloError }}</p>
          </div>
          <div v-else class="text-xs text-gray-500 italic bg-white p-2 rounded border border-gray-100 text-center">
            Verificación omitida. El pago se registrará manualmente.
          </div>
        </div>

        <!-- Campo referencia si aplica -->
        <text-input
          v-if="!payment_method.change" v-model="form.reference" class="mb-4" label="Referencia"
          placeholder="Ingrese el número de referencia" :error="form.errors.reference"
        />

        <!-- Campo voucher (comprobante de pago) -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-1">Comprobante (voucher)</label>
          <input
            type="file" accept="image/*" class="form-input w-full rounded-lg border-gray-300"
            @change="onVoucherChange"
          />
          <div v-if="voucherPreview" class="mt-2">
            <img :src="voucherPreview" alt="Voucher preview" class="h-20 rounded shadow border mx-auto" />
          </div>
        </div>

        <text-input v-model="form.date" type="date" class="mb-4" label="Fecha de pago" />

        <!-- Mostrar cambio si excede el balance -->
        <div v-if="hasChange" class="p-4 bg-blue-50 rounded-lg mb-4">
          <p class="text-md font-bold text-blue-900">
            Cambio a devolver: $ {{ changeAmount.toFixed(2) }} (Bs. {{ changeAmount.toFixed(2) * change }})
          </p>
        </div>

        <!-- Botones de acción -->
        <div class="flex items-center justify-end gap-4">
          <button
            type="button" class="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors"
            @click="cancelPayment"
          >
            Cancelar
          </button>
          <button type="submit" class="px-6 py-2 bg-teal-800 text-white rounded-lg hover:bg-teal-900 transition-colors">
            Procesar pago
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import TextInput from './TextInput.vue'

export default {
  components: {
    TextInput,
  },

  props: {
    payment_methods: {
      type: Object,
      required: true,
    },
    balance: {
      type: Number,
      required: true,
    },
    change: {
      type: Number,
      required: true,
    },
    order: {
      type: Object,
      required: true,
    },
    quick: {
      type: Boolean,
      default: false,
    },
    client: {
      type: Object,
      default: null,
    },
    splitAmount: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      voucherPreview: null,
      mode: 'select',
      payment_method: null,
      form: this.$inertia.form({
        method: '',
        amount: 0,
        reference: '',
        quick: true,
        order_id: null,
        amount_bs: 0,
        client_id: this.client?.id || null,
        date: '',
      }),
      amountAlternate: 0,
      lastEdited: 'usd', // 'usd' o 'bs'
      isVerifying: false,
      verifiedPabilo: false,
      bypassPabilo: false,
      pabiloError: null,
    }
  },

  computed: {
    hasChange() {
      return this.balance < this.form.amount
    },

    changeAmount() {
      return this.form.amount - this.balance
    },
    isAdmin() {
      const role = this.$page.props.auth?.user?.role
      return role === 'admin' || role === 'superadmin'
    },
    isPagoMovilBs() {
      return this.payment_method && (this.payment_method.id === 5)
    },
    hasPabiloCredentials() {
      if (this.$page.props.auth?.account) {
        return this.$page.props.auth.account.pabilo_enabled
      }
      if (this.$page.props.pabilo_enabled !== undefined) {
        return this.$page.props.pabilo_enabled
      }
      return false
    },
  },

  watch: {
    amountAlternate(val) {
      if (this.payment_method && this.payment_method.change_currency_id && this.lastEdited === 'bs') {
        this.form.amount = val / this.change
      }
    },
    'form.amount'(val) {
      if (this.payment_method && this.payment_method.change_currency_id && this.lastEdited === 'usd') {
        this.amountAlternate = val * this.change
      }
    },
    change(val) {
      if (this.payment_method && this.payment_method.change_currency_id) {
        if (this.lastEdited === 'usd') {
          this.amountAlternate = this.form.amount * val
        } else if (this.lastEdited === 'bs') {
          this.form.amount = this.amountAlternate / val
        }
      }
    },
  },

  methods: {
    onVoucherChange(e) {
      const file = e.target.files[0]
      if (file) {
        this.form.voucher = file
        this.voucherPreview = URL.createObjectURL(file)
      } else {
        this.form.voucher = null
        this.voucherPreview = null
      }
    },
    resetForm() {
      this.mode = 'select'
      this.payment_method = null
      this.form = this.$inertia.form({
        method: '',
        amount: 0,
        reference: '',
        quick: true,
        order_id: null,
        amount_bs: 0,
        client_id: this.client?.id || null,
        date: '', 
      })
      this.amountAlternate = 0
      this.isVerifying = false
      this.verifiedPabilo = false
      this.bypassPabilo = false
      this.pabiloError = null
    },

    applySplitAmount() {
      if (this.splitAmount > 0) {
        const val = parseFloat(Math.min(this.splitAmount, this.balance).toFixed(2))
        this.lastEdited = 'usd'
        this.form.amount = val
        if (this.change && this.change > 0) {
          this.amountAlternate = parseFloat((val * this.change).toFixed(2))
        }
      }
    },

    applyFullBalance() {
      if (this.balance > 0) {
        const val = parseFloat(this.balance.toFixed(2))
        this.lastEdited = 'usd'
        this.form.amount = val
        if (this.change && this.change > 0) {
          this.amountAlternate = parseFloat((val * this.change).toFixed(2))
        }
      }
    },

    selectPaymentMethod(method) {
      this.mode = 'form'
      this.payment_method = method

      let target = 0
      if (this.splitAmount && this.splitAmount > 0) {
        target = Math.min(this.splitAmount, this.balance)
      } else if (this.balance > 0) {
        target = this.balance
      }

      this.lastEdited = 'usd'
      this.form.amount = parseFloat(target.toFixed(2))
      if (this.change && this.change > 0) {
        this.amountAlternate = parseFloat((target * this.change).toFixed(2))
      }
    },

    cancelPayment() {
      this.mode = 'select'
    },

    validatePayment() {
      if (!this.payment_method.change && !this.form.reference) {
        alert('Debe ingresar una referencia')
        return false
      }

      if (this.isPagoMovilBs && this.hasPabiloCredentials && !this.bypassPabilo && !this.verifiedPabilo) {
        alert('Debe verificar el pago móvil con Pabilo antes de procesar.')
        return false
      }

      if(this.order){
        const totalPayment = parseInt(this.form.amount) + parseInt(this.order.balance)

        if (this.order.total < totalPayment) {
          const newAmount = this.form.amount - (parseFloat(this.form.amount) - parseFloat(this.order.total))

          if (parseInt(newAmount) + parseInt(this.order.balance) > this.order.total) {
            alert('El monto del pago excede el balance pendiente')
            return false
          }

          this.form.amount = newAmount 
        }
      }

      return true
    },

    async verifyPabilo() {
      if (!this.form.reference || (!this.form.amount && !this.amountAlternate)) {
        this.pabiloError = 'Por favor complete: Monto y Referencia antes de verificar.'
        return
      }
      this.pabiloError = null
      this.isVerifying = true
      
      const payload = {
        amount: this.payment_method.change_currency_id ? this.amountAlternate : this.form.amount,
        reference: this.form.reference,
      }

      try {
        let url = '/pabilo/verify'
        if (this.quick && this.order && this.order.uuid) {
          url = `/public/orders/${this.order.uuid}/pabilo/verify`
        }
         
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.head.querySelector('meta[name="csrf-token"]')?.content,
          },
          body: JSON.stringify(payload),
        })
         
        const data = await response.json()

        if (response.ok && data.success) {
          this.verifiedPabilo = true
        } else {
          this.pabiloError = data.message || 'Error al verificar.'
        }
      } catch (error) {
        this.pabiloError = error.message || 'Error de conexión con Pabilo.'
      } finally {
        this.isVerifying = false
      }
    },

    async processPay() {
      console.log(this.form)
      // Validar que si hay voucher sea imagen
      if (this.form.voucher && this.form.voucher.type && !this.form.voucher.type.startsWith('image/')) {
        alert('El comprobante debe ser una imagen válida.')
        return
      }
      if (!this.validatePayment()) return
      // Si es flujo público (quick y order tiene uuid)
      if (this.quick && this.order && this.order.uuid) {
        try {
          const formData = new FormData()
          formData.append('method', this.payment_method.id || this.payment_method.name)
          formData.append('amount', this.form.amount)
          formData.append('reference', this.form.reference || '')
          formData.append('date', this.form.date || '')
          if (this.form.voucher) {
            formData.append('voucher', this.form.voucher)
          }
          const resp = await fetch(`/orders/public/${this.order.uuid}/pay`, {
            method: 'POST',
            body: formData,
          })
          const data = await resp.json()
          if (data.success) {
            this.$emit('success')
            this.resetForm()
          } else {
            alert('Hubo un error al registrar el pago.')
          }
        } catch (e) {
          alert('Hubo un error al registrar el pago.')
        }
        return
      }
      // Aquí va la lógica normal
      this.form.method = this.payment_method.name
      if(this.order){
        this.form.order_id = this.order.id
      }
      this.form.amount_bs = this.amountAlternate

      if(this.order){
        this.form.post('/orders/pay')
      }else {
        this.form.post('/payments')
      }
        
      this.resetForm()
    },
  },
}
</script>